import React from 'react';
import { Trans } from 'react-i18next';
import { TFunction } from 'i18next';
import { Highlighted, TrustCertificates, ListItem } from 'react-design-components';
import { makeStyles, Theme, createStyles } from '@material-ui/core';
import { PartnerProduct } from '../../../services';
import { useGlobalState } from '../../../state/persistence';
import { useGetPartnerProducts, useGetSelectedLegalInsurance } from '../../Shared';
import useGetContradictionTranslationKeys from './useGetContradictionTranslationKeys';
import { HTMLParsedText } from '../../../components/Shared';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    trust: {
      [theme.breakpoints.down('xs')]: {
        display: 'flex',
        justifyContent: 'center',
      },
    },
    red: {
      color: theme.palette.error.main,
      fontWeight: 700,
    },
  }),
);

const useGetListItems = (t: TFunction): Array<ListItem> => {
  const classes = useStyles();
  const { question } = useGlobalState();
  const periodYear = question.periodYearTo || new Date().getFullYear();
  const dateOfDelivery = new Date(question.deliverAt || '');
  const partnerProducts = useGetPartnerProducts() || [];
  const freePartnerProduct = partnerProducts.find((p: PartnerProduct) => !p.requiresPayment);
  const legalInsuranceItems: Array<ListItem> = [];
  const { contractDictionByPeriodKey, contractDictionByDeliveryKey } = useGetContradictionTranslationKeys(
    periodYear,
    dateOfDelivery,
  );
  const legalInsurance = useGetSelectedLegalInsurance();

  if (freePartnerProduct && question.legalInsurance && !legalInsurance) {
    legalInsuranceItems.push({
      id: 'legalInsurance',
      title: freePartnerProduct.integratedPartner.preResultPromoText ? (
        <HTMLParsedText html={freePartnerProduct.integratedPartner.preResultPromoText} />
      ) : (
        <Trans
          t={t}
          i18nKey="list.legal_insurance_partner"
          values={{
            name: freePartnerProduct.integratedPartner.name,
          }}
        >
          <strong>name</strong>
        </Trans>
      ),
      icon: 'dotted',
    });
  } else if (legalInsurance) {
    const hasName = legalInsurance !== 'not_in_list';
    if (hasName) {
      legalInsuranceItems.push({
        id: 'legalInsurance',
        title: (
          <Trans
            t={t}
            i18nKey="list.legal_insurance"
            values={{
              name: legalInsurance,
            }}
            components={{ italic: <i />, underline: <u />, bold: <b /> }}
          >
            <strong>name</strong>
          </Trans>
        ),
        icon: 'dotted',
      });
    } else {
      legalInsuranceItems.push({
        id: 'legalInsurance',
        title: t('list.legal_insurance_not_in_list'),
        icon: 'dotted',
      });
    }
  }

  return [
    {
      id: 'periodYear',
      title: (
        <Trans
          t={t}
          i18nKey="list.period_year"
          values={{
            periodYear,
            contradiction: t(contractDictionByPeriodKey),
          }}
          components={{ red: <span className={classes.red} />, Highlighted: <Highlighted /> }}
        />
      ),
      icon: 'dotted',
    },
    {
      id: 'dateOfDelivery',
      title: (
        <Trans
          t={t}
          i18nKey="list.date_of_delivery"
          values={{
            dateOfDelivery: dateOfDelivery.toLocaleDateString('de'),
            contradiction: t(contractDictionByDeliveryKey),
          }}
          components={{
            red: <span className={classes.red} />,
            Highlighted: <Highlighted />,
          }}
        />
      ),
      icon: 'dotted',
    },
    {
      id: 'percentWrong',
      title: (
        <Trans t={t} i18nKey="list.percent_wrong">
          <Highlighted>percent</Highlighted>
          <Highlighted>wrong</Highlighted>
        </Trans>
      ),
      icon: 'dotted',
    },
    {
      id: 'average',
      title: (
        <Trans t={t} i18nKey="list.average">
          <Highlighted>average</Highlighted>
        </Trans>
      ),
      icon: 'dotted',
    },
    ...legalInsuranceItems,
    {
      id: 'trust',
      title: (
        <div className={classes.trust}>
          <TrustCertificates />
        </div>
      ),
    },
    {
      id: 'start_check',
      title: t('list.start_check'),
      icon: 'checked',
    },
  ];
};

export default useGetListItems;
